import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { UsersIcon, ClockIcon, CheckCircleIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon } from 'lucide-react';
export function OperationalAnalyticsReport({
  filters,
  locale,
  report
}) {
  // Sample data for user productivity
  const userData = [{
    id: 'USR-001',
    name: 'John Smith',
    department: 'Finance',
    tasksCompleted: 145,
    avgProcessingTime: 2.3,
    exceptionRate: 3.2
  }, {
    id: 'USR-002',
    name: 'Sarah Johnson',
    department: 'Procurement',
    tasksCompleted: 178,
    avgProcessingTime: 1.8,
    exceptionRate: 2.1
  }, {
    id: 'USR-003',
    name: 'Michael Brown',
    department: 'Accounts Payable',
    tasksCompleted: 132,
    avgProcessingTime: 2.5,
    exceptionRate: 4.7
  }, {
    id: 'USR-004',
    name: 'Emily Davis',
    department: 'Finance',
    tasksCompleted: 156,
    avgProcessingTime: 2.0,
    exceptionRate: 2.8
  }, {
    id: 'USR-005',
    name: 'Robert Wilson',
    department: 'Procurement',
    tasksCompleted: 112,
    avgProcessingTime: 3.1,
    exceptionRate: 5.2
  }];
  // Processing time trend data
  const processingTimeTrendData = [{
    month: 'Jan',
    time: 3.2
  }, {
    month: 'Feb',
    time: 2.8
  }, {
    month: 'Mar',
    time: 2.5
  }, {
    month: 'Apr',
    time: 2.2
  }, {
    month: 'May',
    time: 1.9
  }, {
    month: 'Jun',
    time: 1.8
  }];
  // Document processing by type
  const documentProcessingData = [{
    type: 'Invoices',
    count: 280
  }, {
    type: 'Purchase Orders',
    count: 175
  }, {
    type: 'Requisitions',
    count: 120
  }, {
    type: 'Receipts',
    count: 95
  }, {
    type: 'Contracts',
    count: 45
  }];
  // System usage by module
  const moduleUsageData = [{
    module: 'AP',
    users: 42,
    transactions: 320
  }, {
    module: 'Procurement',
    users: 38,
    transactions: 280
  }, {
    module: 'Vendors',
    users: 25,
    transactions: 150
  }, {
    module: 'Reporting',
    users: 35,
    transactions: 210
  }, {
    module: 'Settings',
    users: 12,
    transactions: 45
  }];
  // Use report title and description if provided, otherwise use defaults
  const reportTitle = report?.name || 'User Productivity Report';
  const reportDescription = report?.description || 'Analysis of user productivity, process efficiency, and system performance';
  return <div className="p-6" data-id="element-16146">
      <div className="mb-8" data-id="element-16147">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-16148">{reportTitle}</h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-16149">{reportDescription}</p>
      </div>
      {/* Key metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-16150">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16151">
          <div className="p-5" data-id="element-16152">
            <div className="flex items-center" data-id="element-16153">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3" data-id="element-16154">
                <UsersIcon className="h-6 w-6 text-white" data-id="element-16155" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16156">
                <dl data-id="element-16157">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16158">
                    Active Users
                  </dt>
                  <dd data-id="element-16159">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16160">42</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-16161">
            <div className="text-sm" data-id="element-16162">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-16163">
                View all users
              </a>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16164">
          <div className="p-5" data-id="element-16165">
            <div className="flex items-center" data-id="element-16166">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-16167">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-16168" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16169">
                <dl data-id="element-16170">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16171">
                    Avg. Processing Time
                  </dt>
                  <dd data-id="element-16172">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16173">
                      1.8 days
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-16174">
            <div className="text-sm" data-id="element-16175">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-16176">
                View processing details
              </a>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16177">
          <div className="p-5" data-id="element-16178">
            <div className="flex items-center" data-id="element-16179">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-16180">
                <CheckCircleIcon className="h-6 w-6 text-white" data-id="element-16181" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16182">
                <dl data-id="element-16183">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16184">
                    System Uptime
                  </dt>
                  <dd data-id="element-16185">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16186">
                      99.97%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-16187">
            <div className="text-sm" data-id="element-16188">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-16189">
                View system status
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-16190">
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-16191">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-16192">
            Document Processing by Type
          </h4>
          <div className="h-80" data-id="element-16193">
            <ResponsiveContainer width="100%" height="100%" data-id="element-16194">
              <BarChart data={documentProcessingData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-16195">
                <CartesianGrid strokeDasharray="3 3" data-id="element-16196" />
                <XAxis dataKey="type" data-id="element-16197" />
                <YAxis data-id="element-16198" />
                <Tooltip data-id="element-16199" />
                <Bar dataKey="count" fill="#4f46e5" data-id="element-16200" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-16201">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-16202">
            Average Processing Time Trend (Days)
          </h4>
          <div className="h-80" data-id="element-16203">
            <ResponsiveContainer width="100%" height="100%" data-id="element-16204">
              <LineChart data={processingTimeTrendData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-16205">
                <CartesianGrid strokeDasharray="3 3" data-id="element-16206" />
                <XAxis dataKey="month" data-id="element-16207" />
                <YAxis data-id="element-16208" />
                <Tooltip data-id="element-16209" />
                <Line type="monotone" dataKey="time" stroke="#10b981" activeDot={{
                r: 8
              }} data-id="element-16210" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* User Productivity Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-16211">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-16212">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-16213">
            User Productivity Metrics
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-16214">
            Performance metrics by user for the selected time period
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-16215">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-16216">
            <thead className="bg-gray-50" data-id="element-16217">
              <tr data-id="element-16218">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16219">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16220">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16221">
                  Tasks Completed
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16222">
                  Avg. Processing Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16223">
                  Exception Rate
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16224">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-16225">
              {userData.map(user => <tr key={user.id} data-id="element-16226">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-16227">
                    {user.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16228">
                    {user.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16229">
                    {user.tasksCompleted}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16230">
                    {user.avgProcessingTime} days
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16231">
                    {user.exceptionRate}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-16232">
                    <div className="flex items-center" data-id="element-16233">
                      {user.avgProcessingTime < 2.0 ? <TrendingUpIcon className="h-5 w-5 text-green-500 mr-1.5" data-id="element-16234" /> : user.avgProcessingTime < 2.5 ? <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-1.5" data-id="element-16235" /> : <AlertCircleIcon className="h-5 w-5 text-yellow-500 mr-1.5" data-id="element-16236" />}
                      <span className={`text-sm font-medium ${user.avgProcessingTime < 2.0 ? 'text-green-700' : user.avgProcessingTime < 2.5 ? 'text-blue-700' : 'text-yellow-700'}`} data-id="element-16237">
                        {user.avgProcessingTime < 2.0 ? 'Excellent' : user.avgProcessingTime < 2.5 ? 'Good' : 'Needs Improvement'}
                      </span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}